import { sendToApi } from 'api/client'
import { PaginatedApiClient } from 'api/PaginatedApiClient'
import { Filters } from 'screens/Finder/components/ViewPreferences/components/ViewModifierControls/Filter/Filter'
import { AlmanacFile as File, CalendarDayGridViewType, ViewType } from 'types'
import { Property } from './PropertyClient'
import { DayGridType } from 'screens/Finder/components/views/CalendarView/utils'

export enum SortingDirection {
  Ascending = 'ascending',
  Descending = 'descending',
}

export enum GroupingDirection {
  Ascending = 'group_ascending',
  Descending = 'group_descending',
}

export const ViewTypeToViewName: Record<ViewType, string> = {
  [ViewType.List]: 'List',
  [ViewType.Table]: 'Database',
  [ViewType.Board]: 'Board',
  [ViewType.Gallery]: 'Gallery',
  [ViewType.Folder]: 'Folder',
  [ViewType.Calendar]: 'Calendar',
}

export const FullCalendarDayGridToCalendarDayGridView: Record<DayGridType, CalendarDayGridViewType> = {
  dayGridMonth: CalendarDayGridViewType.Month,
  dayGridWeek: CalendarDayGridViewType.Week,
  timeGridWeek: CalendarDayGridViewType.Week,
}

export const CalendarDayGridViewToFullCalendarDayGrid: Record<CalendarDayGridViewType, DayGridType> = {
  [CalendarDayGridViewType.Month]: 'dayGridMonth',
  [CalendarDayGridViewType.Week]: 'timeGridWeek',
}

export interface SchemaAttributes {
  id: number
  name: string
  sorting_direction: SortingDirection
  sorting_property_id: number | null
  default_view: ViewType
  calendar_day_grid_view: CalendarDayGridViewType
  grouping_direction: GroupingDirection
  grouping_property_id: number | null
  calendar_property_id: number | null
  properties: Property[]
  historical_properties: Property[]
  /** A `Record` in the form `{[propertyId]: width}`, with the only exception being the "Name" column, since it does
   * not have a propertyId, and thus uses the key `name` instead.
   */
  column_widths: Record<string, number>
  filters?: Filters
  show_tasks: boolean
}

export type SortingDescriptor = Pick<SchemaAttributes, 'sorting_property_id' | 'sorting_direction'>

export interface FilesViewSchema extends SchemaAttributes {
  name: string
  accessing_as_super_admin: boolean
  workspace: {
    key: string
  }
  project: {
    key: string
  } | null
  grouping_property?: Property
  sorting_property?: Property
  workflow_property?: Property
}

export interface FilesCounts {
  today: number
  yesterday: number
  thisWeek: number
  lastWeek: number
  thisMonth: number
  lastMonth: number
  older: number
}

export interface MinimalFilesViewSchema {
  id: number
  type: ViewType
  name: string
  calendar_property_id?: number | null
  creator?: string
}

export interface FilesView<FileType = File> extends BasicFilesView<FileType> {
  schema: FilesViewSchema
  default_schema: FilesViewSchema
  all_schemas: MinimalFilesViewSchema[]
}

export interface BasicFilesView<FileType = File> {
  files: FileType[]
}

interface CreatePropertyInput {
  propertyId: number
  position?: number
}

interface UpdatePropertyInput extends CreatePropertyInput {}

export const FilesViewsClient = {
  show: (workspaceId: number, projectId: string = 'root', options: Partial<SchemaAttributes> = {}) => {
    const searchParams = new URLSearchParams()
    if (options.sorting_property_id) {
      searchParams.set('sorting_property_id', `${options.sorting_property_id}`)
    }
    if (options.sorting_direction) {
      searchParams.set('sorting_direction', options.sorting_direction)
    }

    return new PaginatedApiClient<FilesView>(`/w/${workspaceId}/files_views/${projectId}?${searchParams.toString()}`, {
      method: 'GET',
      items: 50,
    })
  },

  recent: (workspaceId: number) => {
    return new PaginatedApiClient<FilesView>(`/w/${workspaceId}/files_views/recent`, {
      method: 'GET',
      items: 50,
    })
  },

  yourFiles: (workspaceId: number) => {
    return new PaginatedApiClient<FilesView>(`/w/${workspaceId}/files_views/your_files`, {
      method: 'GET',
      items: 50,
    })
  },

  importedFiles: (workspaceId: number) => {
    return new PaginatedApiClient<FilesView>(`/w/${workspaceId}/files_views/imported`, {
      method: 'GET',
      items: 50,
    })
  },

  createProperty: async (
    input: CreatePropertyInput,
    workspaceId: number,
    projectId: string = 'root',
    current_schema_id: number,
  ) => {
    const { propertyId, position } = input
    return sendToApi<{ files_view_schema: FilesViewSchema }>(`/w/${workspaceId}/files_views/${projectId}/properties`, {
      method: 'POST',
      body: {
        current_schema_id,
        property_id: propertyId,
        position,
      },
    })
  },

  updateProperty: async (
    input: UpdatePropertyInput,
    workspaceId: number,
    projectId: string = 'root',
    currentSchemaId: number,
  ) => {
    const { propertyId, position } = input
    return sendToApi<{ files_view_schema: FilesViewSchema }>(
      `/w/${workspaceId}/files_views/${projectId}/properties/${propertyId}`,
      {
        method: 'PUT',
        body: {
          current_schema_id: currentSchemaId,
          position,
        },
      },
    )
  },

  destroyProperty: async (
    propertyId: number,
    workspaceId: number,
    projectId: string = 'root',
    current_schema_id: number,
  ) => {
    return sendToApi<{ files_view_schema: FilesViewSchema }>(
      `/w/${workspaceId}/files_views/${projectId}/properties/${propertyId}?current_schema_id=${current_schema_id}`,
      {
        method: 'DELETE',
      },
    )
  },

  destroyHistoricalProperty: async (
    propertyId: number,
    workspaceId: number,
    projectId: string = 'root',
    current_schema_id: number,
  ) => {
    return sendToApi<{ files_view_schema: FilesViewSchema }>(
      `/w/${workspaceId}/files_views/${projectId}/historical_properties/${propertyId}?current_schema_id=${current_schema_id}`,
      {
        method: 'DELETE',
      },
    )
  },

  getSchemas: async (workspaceId: number, projectId: string) => {
    return sendToApi<{ files_view_schemas: MinimalFilesViewSchema[] }>(
      `/w/${workspaceId}/files_view_schemas?project_id=${projectId}`,
      {
        method: 'GET',
      },
    )
  },

  getSchema: async (workspaceId: number, schemaId: number) => {
    return sendToApi<{ files_view_schema: FilesViewSchema }>(`/w/${workspaceId}/files_view_schemas/${schemaId}`, {
      method: 'GET',
    })
  },

  createSchema: async (
    input: Pick<SchemaAttributes, 'default_view' | 'calendar_property_id' | 'filters' | 'name'> & {
      calendar_property_name?: string | null
    },
    workspaceId: number,
    projectId: string = 'root',
  ) => {
    return sendToApi<{ files_view_schema: FilesViewSchema }>(`/w/${workspaceId}/files_views/${projectId}/schema`, {
      method: 'POST',
      body: input,
    })
  },

  updateSchema: async (
    input: SchemaAttributes & { calendar_property_name?: string | null },
    workspaceId: number,
    projectId: string = 'root',
  ) => {
    return sendToApi<{ files_view_schema: FilesViewSchema }>(`/w/${workspaceId}/files_views/${projectId}/schema`, {
      method: 'PUT',
      body: input,
    })
  },

  makeDefault: async (workspaceId: number, projectId: string, schemaId: number) => {
    return sendToApi<null>(`/w/${workspaceId}/files_views/${projectId}/make_default`, {
      method: 'PUT',
      body: { id: schemaId },
    })
  },

  deleteSchema: async (workspaceId: number, schemaId: number) => {
    return sendToApi<{ files_view_schema: FilesViewSchema }>(`/w/${workspaceId}/files_view_schemas/${schemaId}`, {
      method: 'DELETE',
    })
  },

  duplicateSchema: async (workspaceId: number, schemaId: number) => {
    return sendToApi<{ files_view_schema: MinimalFilesViewSchema }>(
      `/w/${workspaceId}/files_view_schemas/${schemaId}/duplicate`,
      {
        method: 'POST',
      },
    )
  },
}
